import { useTranslations } from "next-intl";
import React from "react";
import Minus from "../../icons/Minus";
import Plus from "../../icons/Plus";
import ButtonIcon from "../ButtonIcon";
import "./index.scss";

const SelectOccupancy = ({
  numberOfAdults,
  numberOfChildren,
  childAges,
  numberOfPets,
  onChangeOccupancy,
  displayNumberOfGuests = false,
  minimumChildGroupAges = [],
  allowsPets = false,
  maximumNumberOfAdults,
  maximumNumberOfChildren,
  maximumNumberOfPets,
}: {
  numberOfAdults: number;
  numberOfChildren: number;
  childAges: number[];
  numberOfPets: number;
  onChangeOccupancy: (
    numberOfAdults: number,
    numberOfChildren: number,
    childAges: number[],
    numberOfPets: number
  ) => void;
  displayNumberOfGuests: boolean;
  minimumChildGroupAges: number[];
  allowsPets: boolean;
  maximumNumberOfAdults: number;
  maximumNumberOfChildren: number;
  maximumNumberOfPets: number;
}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const t = useTranslations("SelectOccupancy");

  const renderChildrenOccupancySection = (
    minimumAge: number,
    maximumAge: number
  ) => {
    const numberOfChildrenInSection = childAges.filter(
      (childAge) => childAge === minimumAge
    ).length;

    return (
      <div className="occupancy children" key={`${minimumAge}-${maximumAge}`}>
        <div className="occupancy-label">
          <div className="label">{t("children")}</div>
          <div className="label-description">
            {minimumAge === maximumAge
              ? t("age-label", { age: minimumAge })
              : t("age-range-label", { minimumAge, maximumAge })}
          </div>
        </div>
        <div className="occupancy-controls">
          <ButtonIcon
            onClick={() => {
              const lastIndex = childAges.lastIndexOf(minimumAge);

              if (lastIndex !== -1) {
                onChangeOccupancy(
                  numberOfAdults,
                  numberOfChildren - 1,
                  [
                    ...childAges.slice(0, lastIndex),
                    ...childAges.slice(lastIndex + 1),
                  ],
                  numberOfPets
                );
              }
            }}
            disabled={numberOfChildrenInSection === 0}
            aria-label={t("decrease-number-of-children")}
          >
            <Minus />
          </ButtonIcon>
          <div className="occupancy-value">{numberOfChildrenInSection}</div>
          <ButtonIcon
            onClick={() => {
              onChangeOccupancy(
                numberOfAdults,
                numberOfChildren + 1,
                [...childAges, minimumAge],
                numberOfPets
              );
            }}
            aria-label={t("increase-number-of-children")}
            disabled={numberOfChildren === maximumNumberOfChildren}
          >
            <Plus />
          </ButtonIcon>
        </div>
      </div>
    );
  };

  const sortedMinimumChildGroupAges = [...minimumChildGroupAges]
    .sort((a, b) => a - b)
    .reverse();
  const childrenOccupancySectionNodes: React.JSX.Element[] = [];
  let currentMaximumChildAge = 17;
  for (
    let childGroupAgeIndex = 0;
    childGroupAgeIndex < sortedMinimumChildGroupAges.length;
    childGroupAgeIndex++
  ) {
    const childGroupAge = sortedMinimumChildGroupAges[childGroupAgeIndex];
    childrenOccupancySectionNodes.push(
      renderChildrenOccupancySection(childGroupAge, currentMaximumChildAge)
    );
    currentMaximumChildAge = childGroupAge - 1;
  }

  return (
    <div className="component select-occupancy">
      <div className="select-occupancy-inner-wrapper">
        <div className="occupancy number-of-adults">
          <div className="occupancy-label">
            <div className="label">
              {displayNumberOfGuests ? t("guests") : t("adults")}
            </div>
            {displayNumberOfGuests === false && (
              <div className="label-description">{t("ages-18-plus")}</div>
            )}
          </div>
          <div className="occupancy-controls">
            <ButtonIcon
              onClick={() => {
                if (numberOfAdults > 1) {
                  onChangeOccupancy(
                    numberOfAdults - 1,
                    numberOfChildren,
                    childAges,
                    numberOfPets
                  );
                }
              }}
              disabled={numberOfAdults === 1}
              aria-label={t("decrease-number-of-adults")}
            >
              <Minus />
            </ButtonIcon>
            <div className="occupancy-value">{numberOfAdults}</div>
            <ButtonIcon
              onClick={() => {
                onChangeOccupancy(
                  numberOfAdults + 1,
                  numberOfChildren,
                  childAges,
                  numberOfPets
                );
              }}
              aria-label={t("increase-number-of-adults")}
              disabled={numberOfAdults === maximumNumberOfAdults}
            >
              <Plus />
            </ButtonIcon>
          </div>
        </div>

        {displayNumberOfGuests === false && childrenOccupancySectionNodes}

        {allowsPets && (
          <div className="occupancy number-of-pets">
            <div className="occupancy-label">
              <div className="label">{t("pets")}</div>
              <div className="label-description">{/*Service stuff here*/}</div>
            </div>
            <div className="occupancy-controls">
              <ButtonIcon
                onClick={() => {
                  if (numberOfPets > 0) {
                    onChangeOccupancy(
                      numberOfAdults,
                      numberOfChildren,
                      childAges,
                      numberOfPets - 1
                    );
                  }
                }}
                disabled={numberOfPets === 0}
                aria-label={t("decrease-number-of-pets")}
              >
                <Minus />
              </ButtonIcon>
              <div className="occupancy-value">{numberOfPets}</div>
              <ButtonIcon
                onClick={() => {
                  onChangeOccupancy(
                    numberOfAdults,
                    numberOfChildren,
                    childAges,
                    numberOfPets + 1
                  );
                }}
                aria-label={t("increase-number-of-pets")}
                disabled={numberOfPets === maximumNumberOfPets}
              >
                <Plus />
              </ButtonIcon>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectOccupancy;
